import React, { createContext, useContext, useEffect, useState } from 'react'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser, selectIsAuthenticated, selectIsOfflineMode } from '@/redux/reducers/auth.reducer'
import { selectAppSettings } from '@/redux/reducers/app.reducer'
import { offlineDatabase, encryptionService, syncService } from '@/core/offline'
import { OFFLINE_AUTH_KEY } from '@/constants/offline-auth'

type OfflineModeContextType = {
  isOfflineMode: boolean
  canAccessOfflineMode: boolean
  lastOnlineSync: Date | null
  timeUntilExpiry: number | null // hours until offline access expires
  isOfflineDatabaseReady: boolean
}

const OfflineModeContext = createContext<OfflineModeContextType>({
  isOfflineMode: false,
  canAccessOfflineMode: false,
  lastOnlineSync: null,
  timeUntilExpiry: null,
  isOfflineDatabaseReady: false,
})

export const useOfflineMode = () => useContext(OfflineModeContext)

const OFFLINE_ACCESS_DURATION_HOURS = 48
const LAST_ONLINE_SYNC_KEY = 'offline_last_sync'
const OFFLINE_DB_INITIALIZED_KEY = 'offline_db_initialized'
const OFFLINE_DB_USER_ID_KEY = 'offline_db_user_id'
const SYNC_SETTLE_TIMEOUT_MS = 15_000

/**
 * Resolves once a sync run that started after this call reports inactive, or after a
 * timeout (the sync service completes asynchronously, after `syncNow()` resolves).
 */
function waitForSyncToSettle(): Promise<void> {
  return new Promise((resolve) => {
    let sawActive = false
    const finish = () => {
      clearTimeout(timer)
      unsubscribe()
      resolve()
    }
    const timer = setTimeout(() => {
      console.warn('Offline sync did not settle before the cache reset; continuing')
      finish()
    }, SYNC_SETTLE_TIMEOUT_MS)
    const unsubscribe = syncService.onSyncStatusChange((status) => {
      if (status.isActive) sawActive = true
      else if (sawActive) finish()
    })
  })
}

/**
 * Opens the offline cache with the key derived from the previous user id, pushes any
 * unsynced records to the server, then wipes the cache so it can be rebuilt under the
 * new id. Without the push step, the id flip would silently drop offline work.
 */
async function migrateOfflineCacheToNewUserId(previousUserId: string, keyForUser: (userId: string) => string): Promise<void> {
  await encryptionService.initialize(keyForUser(previousUserId))
  await offlineDatabase.initialize()
  try {
    const settled = waitForSyncToSettle()
    await syncService.syncNow()
    await settled
  } catch (error) {
    console.warn('Could not push unsynced offline data before resetting the cache:', error)
  }
  await syncService.clearAllOfflineData()
  await offlineDatabase.close()
}

// Wrapper component that conditionally uses Keycloak
function OfflineModeProviderInner({ children }: { children: React.ReactNode }) {
  const [isOfflineMode, setIsOfflineMode] = useState(!navigator.onLine)
  const [lastOnlineSync, setLastOnlineSync] = useState<Date | null>(null)
  const [isOfflineDatabaseReady, setIsOfflineDatabaseReady] = useState(false)

  const authUser = useAppSelector(selectAuthUser)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const isReduxOfflineMode = useAppSelector(selectIsOfflineMode)
  const appSettings = useAppSelector(selectAppSettings)

  // Do not use Keycloak hook here to avoid rules-of-hooks violations across modes
  // Rely on Redux auth state and local storage only

  // Initialize last online sync from localStorage
  useEffect(() => {
    const storedLastSync = localStorage.getItem(LAST_ONLINE_SYNC_KEY)
    if (storedLastSync) {
      setLastOnlineSync(new Date(storedLastSync))
    }
  }, [])

  // Initialize offline database when authenticated
  useEffect(() => {
    const initializeOfflineDatabase = async () => {
      if (!isAuthenticated || !authUser) {
        return
      }

      try {
        // Check if database was already initialized for this user
        const dbInitialized = localStorage.getItem(`${OFFLINE_DB_INITIALIZED_KEY}_${authUser._id}`)

        const keyForUser = (userId: string) => (isReduxOfflineMode ? `${userId}_offline` : `${userId}_online`)
        const userIdKey = authUser.email ? `${OFFLINE_DB_USER_ID_KEY}_${authUser.email}` : null
        const previousId = userIdKey ? localStorage.getItem(userIdKey) : null

        // One-time migration when the server changed its id representation for this user
        // (legacy ObjectId → encrypted external id). Cached records are encrypted with a key
        // derived from the previous id, so they are pushed with that key before being wiped.
        // Only possible online: an offline login reuses the cached (previous) id anyway.
        if (previousId && previousId !== authUser._id && !isReduxOfflineMode) {
          await migrateOfflineCacheToNewUserId(previousId, keyForUser)
          localStorage.removeItem(`${OFFLINE_DB_INITIALIZED_KEY}_${previousId}`)
        }

        // Initialize encryption with user-specific key, then the database
        await encryptionService.initialize(keyForUser(authUser._id))
        await offlineDatabase.initialize()

        if (userIdKey && !isReduxOfflineMode) {
          localStorage.setItem(userIdKey, authUser._id)
        }

        if (!dbInitialized) {
          // Mark as initialized for this user
          localStorage.setItem(`${OFFLINE_DB_INITIALIZED_KEY}_${authUser._id}`, 'true')
        }

        setIsOfflineDatabaseReady(true)
      } catch (error) {
        console.error('Failed to initialize offline database:', error)
        setIsOfflineDatabaseReady(false)
      }
    }

    initializeOfflineDatabase()
  }, [isAuthenticated, authUser, isReduxOfflineMode])

  // Clean up database on logout
  useEffect(() => {
    if (!isAuthenticated && isOfflineDatabaseReady) {
      offlineDatabase.close()
      setIsOfflineDatabaseReady(false)
    }
  }, [isAuthenticated, isOfflineDatabaseReady])

  // When DB becomes ready and we are online, trigger a smart sync once
  useEffect(() => {
    if (isOfflineDatabaseReady && navigator.onLine) {
      try {
        // Scan, re-queue failed items, then sync
        syncService.syncNow()
      } catch (e) {
        console.warn('Failed to trigger sync after DB ready:', e)
      }
    }
  }, [isOfflineDatabaseReady])

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOfflineMode(false)
      const now = new Date()
      setLastOnlineSync(now)
      localStorage.setItem(LAST_ONLINE_SYNC_KEY, now.toISOString())
    }

    const handleOffline = () => {
      // Only auto-switch to offline mode if the setting is enabled
      if (appSettings.autoSwitchOfflineMode) {
        setIsOfflineMode(true)
      }
    }

    // Initial check - only set offline mode if auto-switch is enabled
    if (!navigator.onLine && appSettings.autoSwitchOfflineMode) {
      setIsOfflineMode(true)
    } else if (navigator.onLine) {
      setIsOfflineMode(false)
    }

    if (navigator.onLine && !isReduxOfflineMode) {
      handleOnline()
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [isReduxOfflineMode, appSettings.autoSwitchOfflineMode])

  // Calculate time until offline access expires
  const timeUntilExpiry = React.useMemo(() => {
    if (!lastOnlineSync) return null

    const hoursSinceLastSync = (new Date().getTime() - lastOnlineSync.getTime()) / (1000 * 60 * 60)
    const remainingHours = OFFLINE_ACCESS_DURATION_HOURS - hoursSinceLastSync

    return Math.max(0, remainingHours)
  }, [lastOnlineSync])

  // Check if user can access offline mode
  const canAccessOfflineMode = React.useMemo(() => {
    // Must be authenticated
    if (!isAuthenticated || !authUser) return false

    // In offline mode, we don't check Keycloak
    if (isReduxOfflineMode) {
      // Just check if we have cached auth and it's within the time window
      const offlineAuthData = localStorage.getItem(OFFLINE_AUTH_KEY)
      if (!offlineAuthData) return false

      try {
        const authData = JSON.parse(offlineAuthData)
        const lastAuth = new Date(authData.lastOnlineAuth)
        const hoursSinceAuth = (Date.now() - lastAuth.getTime()) / (1000 * 60 * 60)
        return hoursSinceAuth < OFFLINE_ACCESS_DURATION_HOURS
      } catch {
        return false
      }
    }

    // Database must be ready
    if (!isOfflineDatabaseReady) return false

    // Check if we're within the 48-hour offline window
    if (lastOnlineSync) {
      const hoursSinceLastSync = (new Date().getTime() - lastOnlineSync.getTime()) / (1000 * 60 * 60)
      if (hoursSinceLastSync > OFFLINE_ACCESS_DURATION_HOURS) {
        return false
      }
    } else if (!navigator.onLine) {
      // If we've never been online in this session and we're offline, deny access
      return false
    }

    return true
  }, [isAuthenticated, authUser, lastOnlineSync, isOfflineDatabaseReady, isReduxOfflineMode])

  return (
    <OfflineModeContext.Provider
      value={{
        isOfflineMode: isOfflineMode || isReduxOfflineMode,
        canAccessOfflineMode,
        lastOnlineSync,
        timeUntilExpiry,
        isOfflineDatabaseReady,
      }}
    >
      {children}
    </OfflineModeContext.Provider>
  )
}

// Main provider that handles the conditional rendering
export function OfflineModeProvider({ children }: { children: React.ReactNode }) {
  const isReduxOfflineMode = useAppSelector(selectIsOfflineMode)

  // If we're in offline mode, render the provider without Keycloak context
  // Otherwise, it will be rendered inside Keycloak context in the app
  return <OfflineModeProviderInner>{children}</OfflineModeProviderInner>
}
